import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Card, Grid,
  Rating, Checkbox, FormControlLabel, IconButton, Chip, Tooltip, CircularProgress
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import RichTextEditor from '../../../components/common/RichTextEditor';

const createNewIssue = () => ({
  id: Date.now() + Math.random(),
  name: '',
  weight: 25,
  irac: { issue: false, rule: 0, analysis: 0, conclusion: false },
  comment: ''
});

const scoreCalculationTooltip = `
• 'Issue' 체크 시 점수 계산이 시작됩니다. (체크하지 않으면 0점)
• 'Conclusion'을 체크하지 않으면 계산된 점수에서 30%가 감점됩니다.
• 기본 점수는 'Rule'과 'Analysis' 별점 평균을 6점 만점으로 환산하여 계산됩니다.
`;

const calculateIssueScore = (issue) => {
  if (!issue.irac.issue) return 0;

  const ruleScore = issue.irac.rule || 0;
  const analysisScore = issue.irac.analysis || 0;
  let baseScore = (ruleScore + analysisScore) / 2 * (6 / 5);

  if (!issue.irac.conclusion) {
    baseScore *= 0.7;
  }
  
  return baseScore;
};

const MeeEvaluationForm = ({ isSubmitting, onEvaluationSubmit }) => {
  const [evaluations, setEvaluations] = useState([createNewIssue()]);
  const [overallComment, setOverallComment] = useState('');
  const [totalScore, setTotalScore] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);

  const calculateTotals = useCallback(() => {
    const weightSum = evaluations.reduce((acc, issue) => acc + (Number(issue.weight) || 0), 0);
    setTotalWeight(weightSum);

    if (weightSum === 0) {
      setTotalScore(0);
      return;
    }

    const weightedScoreSum = evaluations.reduce((acc, issue) => {
      const issueScore = calculateIssueScore(issue);
      return acc + (issueScore * (Number(issue.weight) || 0));
    }, 0);

    const finalScore = weightSum > 0 ? (weightedScoreSum / weightSum) : 0;
    setTotalScore(finalScore.toFixed(2));
  }, [evaluations]);

  useEffect(() => {
    calculateTotals();
  }, [evaluations, calculateTotals]);

  const handleEvaluationChange = (id, field, value) => {
    setEvaluations(prev => prev.map(issue => {
      if (issue.id === id) {
        if (field === 'irac') {
          return { ...issue, irac: { ...issue.irac, ...value } };
        }
        return { ...issue, [field]: value };
      }
      return issue;
    }));
  };

  const addIssue = () => {
    setEvaluations(prev => [...prev, createNewIssue()]);
  };

  const removeIssue = (id) => {
    setEvaluations(prev => prev.filter(issue => issue.id !== id));
  };

  const prepareSubmitData = (e) => {
    e.preventDefault();
    const submitData = {
      score: totalScore,
      evaluation_details: JSON.stringify(evaluations),
      overall_comment: overallComment,
    };
    onEvaluationSubmit(submitData);
  };

  return (
    <Box component="form" onSubmit={prepareSubmitData}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6">
              종합 점수: {totalScore} / 6.00
            </Typography>
            <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{scoreCalculationTooltip}</div>} arrow>
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Chip 
            label={`가중치 합계: ${totalWeight}%`}
            color={totalWeight === 100 ? 'success' : 'error'} 
            variant="outlined"
          />
        </Box>
        
        {evaluations.map((issue, index) => {
          const issueScore = calculateIssueScore(issue);
          return (
            <Card key={issue.id} sx={{ mb: 2, p: 2, position: 'relative' }} variant="outlined">
              <IconButton 
                aria-label="delete" 
                size="small"
                onClick={() => removeIssue(issue.id)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`쟁점 ${index + 1} 이름`}
                    variant="standard"
                    value={issue.name}
                    onChange={(e) => handleEvaluationChange(issue.id, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="가중치(%)"
                    type="number"
                    variant="standard"
                    value={issue.weight}
                    onChange={(e) => handleEvaluationChange(issue.id, 'weight', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" component="span" sx={{ color: 'primary.main' }}>
                    {issueScore.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" component="span" color="text.secondary">
                    / 6.00
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <FormControlLabel control={<Checkbox checked={issue.irac.issue} onChange={(e) => handleEvaluationChange(issue.id, 'irac', { issue: e.target.checked })} />} label={<Typography variant="body2">Issue Spotting</Typography>} />
                <Box>
                  <Typography component="legend" variant="caption">Rule</Typography>
                  <Rating name={`rule-${issue.id}`} value={issue.irac.rule} onChange={(e, newValue) => handleEvaluationChange(issue.id, 'irac', { rule: newValue })} />
                </Box>
                <Box>
                  <Typography component="legend" variant="caption">Analysis</Typography>
                  <Rating name={`analysis-${issue.id}`} value={issue.irac.analysis} onChange={(e, newValue) => handleEvaluationChange(issue.id, 'irac', { analysis: newValue })} />
                </Box>
                <FormControlLabel control={<Checkbox checked={issue.irac.conclusion} onChange={(e) => handleEvaluationChange(issue.id, 'irac', { conclusion: e.target.checked })} />} label={<Typography variant="body2">Conclusion</Typography>} />
              </Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="standard"
                label="코멘트"
                value={issue.comment}
                onChange={(e) => handleEvaluationChange(issue.id, 'comment', e.target.value)}
                sx={{ mt: 1 }}
              />
            </Card>
          );
        })}
        
        <Button 
          fullWidth 
          variant="outlined" 
          startIcon={<AddCircleOutlineIcon />}
          onClick={addIssue}
          sx={{ mt: 1, mb: 3 }}
        >
          쟁점 추가
        </Button>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>총평 및 조언</Typography>
        <RichTextEditor
          value={overallComment}
          onChange={setOverallComment}
          placeholder="종합적인 평가와 조언을 작성해주세요..."
        />

        <Button 
          type="submit" 
          variant="contained" 
          size="large"
          fullWidth
          disabled={isSubmitting}
          sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : '평가 완료 및 저장'}
        </Button>
    </Box>
  );
};

export default MeeEvaluationForm; 